import { Figure, GoLink, RiIcon, Section, SectionTitle, pick, siteUrlFor } from '@nanning/ui';
import { assetUrl, useT } from '../i18n';
import { creditOf, findImage } from '../useSiteData';
import {WithSiteData} from './common';
import type { SiteData } from '../useSiteData';

function Hero({ d }: { d: SiteData }) {
  const { t, pick: p } = useT();
  const img = findImage(d.imagery, 'hero');
  const credit = creditOf(img);
  return (
    <section aria-labelledby="page-title" className="relative w-full overflow-hidden bg-ink text-on-dark">
      <img
        src={assetUrl(img.local)}
        alt={p(img.alt)}
        loading="eager"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover opacity-75"
      />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-ink via-ink/75 to-ink/25" />
      <div className="relative mx-auto flex min-h-[80vh] max-w-6xl flex-col justify-end px-5 pb-20 pt-40">
        <div className="text-xs font-semibold tracking-widest text-on-dark/80">{t('home.hero.eyebrow')}</div>
        <h1 id="page-title" className="mt-5 max-w-4xl text-4xl font-semibold leading-[1.15] md:text-6xl">
          {p(d.company.slogan)}
        </h1>
        <div className="mt-10 flex flex-wrap gap-3">
          <GoLink
            to="/ecosystem"
            className="inline-flex items-center rounded-md bg-bg px-6 py-3 text-sm font-semibold text-body hover:bg-surface"
          >
            {t('home.hero.cta.ecosystem')}
          </GoLink>
          <GoLink
            to="/about"
            className="inline-flex items-center rounded-md border border-on-dark/50 px-6 py-3 text-sm font-semibold text-on-dark hover:border-on-dark"
          >
            {t('home.hero.cta.about')}
          </GoLink>
        </div>
        {credit && <p className="mt-10 text-xs text-on-dark/70">{`${t('common.imageCredit')}: ${credit}`}</p>}
      </div>
    </section>
  );
}

function EcosystemTeaser({ d }: { d: SiteData }) {
  const { t, pick: p, locale } = useT();
  return (
    <Section id="ecosystem">
      <SectionTitle id="ecosystem" eyebrow={t('home.eco.eyebrow')} title={t('home.eco.title')} lead={t('home.eco.lead')} />
      <ul className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {d.eco.subsidiaries.map((s) => (
          <li key={s.key}>
            <a
              href={siteUrlFor(s.key, locale)}
              className="group flex h-full flex-col rounded-xl border border-line bg-bg p-6 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md focus-visible:outline-2 focus-visible:outline-primary"
            >
              <div className="flex items-start justify-between gap-4">
                <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-on-dark">
                  <RiIcon name={s.mark} className="text-2xl" />
                </span>
              </div>
              <h3 className="mt-5 text-xl font-semibold text-body">{p(s.shortName)}</h3>
              <p className="mt-1 text-xs text-muted">{p(s.legalName)}</p>
              <p className="mt-4 text-sm leading-relaxed text-body">{p(s.positioning)}</p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {s.keywords.map((k, i) => (
                  <li key={i} className="rounded-full bg-surface px-3 py-1 text-xs text-muted-light">
                    {pick(k, locale)}
                  </li>
                ))}
              </ul>
              <span className="mt-auto pt-6 text-sm font-semibold text-primary-light group-hover:underline">
                {t('common.visit')} →
              </span>
            </a>
          </li>
        ))}
        <li className="flex flex-col justify-between rounded-xl bg-ink p-6 text-on-dark">
          <p className="text-lg font-semibold leading-snug">{t('home.about.title')}</p>
          <GoLink to="/about" className="mt-8 inline-flex text-sm font-semibold text-on-dark hover:underline">
            {t('home.about.more')} →
          </GoLink>
        </li>
      </ul>
    </Section>
  );
}

function AboutTeaser({ d }: { d: SiteData }) {
  const { t, pick: p, money } = useT();
  const img = findImage(d.imagery, 'convention');
  const f = d.overview.identity;
  const facts = [
    { k: t('home.facts.founded'), v: f.founded },
    { k: t('home.facts.capital'), v: money(f.registeredCapital) },
    { k: t('home.facts.hq'), v: p(f.registeredAddress) },
    { k: t('home.facts.lines'), v: t('home.facts.linesValue') },
  ];
  return (
    <Section id="about-teaser" tone="surface">
      <div className="grid gap-12 md:grid-cols-2 md:items-center">
        <div>
          <SectionTitle id="about-teaser" eyebrow={t('home.about.eyebrow')} title={t('home.about.title')} />
          <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6">
            {facts.map((x) => (
              <div key={x.k} className="border-t border-line pt-4">
                <dt className="text-xs text-muted">{x.k}</dt>
                <dd className="mt-1 text-base font-semibold text-body">{x.v}</dd>
              </div>
            ))}
          </dl>
          <GoLink to="/about" className="mt-8 inline-flex text-sm font-semibold text-primary-light hover:underline">
            {t('home.about.more')} →
          </GoLink>
        </div>
        <Figure src={assetUrl(img.local)} alt={p(img.alt)} credit={creditOf(img)} ratio="4 / 3" />
      </div>
    </Section>
  );
}

function NewsTeaser({ d }: { d: SiteData }) {
  const { t, pick: p } = useT();
  const items = d.news.items.slice(0, 3);
  return (
    <Section id="news">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionTitle id="news" eyebrow={t('home.news.eyebrow')} title={t('home.news.title')} />
        <GoLink to="/news" className="text-sm font-semibold text-primary-light hover:underline">
          {t('home.news.all')} →
        </GoLink>
      </div>
      <ul className="mt-12 grid gap-4 md:grid-cols-3">
        {items.map((n) => (
          <li key={n.id} className="flex flex-col rounded-xl border border-line bg-bg p-6">
            <div className="flex items-center justify-between gap-3">
              <span className="tnum text-xs text-muted">{n.date}</span>
              <span className="rounded bg-surface px-2 py-0.5 text-xs text-muted-light">{p(n.category)}</span>
            </div>
            <h3 className="mt-4 text-base font-semibold leading-snug text-body">{p(n.title)}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted">{p(n.summary)}</p>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function EsgTeaser({ d }: { d: SiteData }) {
  const { t, pick: p } = useT();
  const img = findImage(d.imagery, 'karst');
  const pillars = [
    { k: t('home.esg.env'), v: t('home.esg.env.body') },
    { k: t('home.esg.social'), v: t('home.esg.social.body') },
    { k: t('home.esg.gov'), v: t('home.esg.gov.body') },
  ];
  return (
    <Section id="esg" tone="surface">
      <div className="grid gap-12 md:grid-cols-2 md:items-center">
        <Figure src={assetUrl(img.local)} alt={p(img.alt)} credit={creditOf(img)} ratio="4 / 3" />
        <div>
          <SectionTitle id="esg" eyebrow={t('home.esg.eyebrow')} title={t('home.esg.title')} />
          <ul className="mt-8 space-y-5">
            {pillars.map((x) => (
              <li key={x.k} className="border-l-2 border-primary pl-4">
                <div className="text-sm font-semibold text-body">{x.k}</div>
                <p className="mt-1 text-sm leading-relaxed text-muted">{x.v}</p>
              </li>
            ))}
          </ul>
          <GoLink to="/esg" className="mt-8 inline-flex text-sm font-semibold text-primary-light hover:underline">
            {t('home.esg.cta')} →
          </GoLink>
        </div>
      </div>
    </Section>
  );
}

function Join() {
  const { t } = useT();
  return (
    <Section id="join" tone="ink">
      <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
        <div className="max-w-2xl">
          <h2 id="join-title" className="text-3xl font-semibold leading-tight md:text-[2.5rem]">
            {t('home.join.title')}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-on-dark/85">{t('home.join.lead')}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <GoLink to="/careers" className="inline-flex rounded-md bg-bg px-6 py-3 text-sm font-semibold text-body hover:bg-surface">
            {t('home.join.cta')}
          </GoLink>
          <GoLink to="/contact" className="inline-flex rounded-md border border-on-dark/50 px-6 py-3 text-sm font-semibold text-on-dark hover:border-on-dark">
            {t('home.join.contact')}
          </GoLink>
        </div>
      </div>
    </Section>
  );
}

export function Home() {
  return (
    <WithSiteData>
      {(d) => (
        <>
          <Hero d={d} />
          <EcosystemTeaser d={d} />
          <AboutTeaser d={d} />
          <NewsTeaser d={d} />
          <EsgTeaser d={d} />
          <Join />
        </>
      )}
    </WithSiteData>
  );
}
